import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, KeyRound } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { evaluatePassword } from "@/lib/passwordPolicy";
import { toast } from "sonner";

/**
 * Página obligatoria cuando `user.must_change_password === true`. Bloquea el
 * acceso al panel hasta que el usuario establece una contraseña que cumple
 * la política. El backend es la fuente de verdad.
 */
export default function ForceChangePasswordPage() {
  const { user, axiosAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const userData = {
    name: user?.name, email: user?.email,
  };
  const evalResult = evaluatePassword(next, { userData, role: user?.role });
  const canSubmit = current && next && next === confirm && evalResult.is_valid && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (!evalResult.is_valid) {
      toast.error(evalResult.reasons[0] || "La contraseña no cumple la política");
      return;
    }
    setLoading(true);
    try {
      await axiosAuth().post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      toast.success("Contraseña actualizada correctamente");
      // Refrescar usuario y redirigir al dashboard
      const me = await axiosAuth().get("/auth/me");
      if (!me.data?.must_change_password) {
        // Forzar refresh local: el AuthContext leerá `must_change_password=false`
        window.location.href = "/admin";
      } else {
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
         data-testid="force-change-password-page">
      <Card className="w-full max-w-md border-amber-300 rounded-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="heading-industrial text-lg font-bold uppercase">Actualiza tu contraseña</h1>
              <p className="text-xs text-slate-500">Antes de continuar debes establecer una contraseña que cumpla la nueva política de seguridad.</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="cur" className="text-xs uppercase tracking-wider">Contraseña actual</Label>
              <Input
                id="cur" type="password" value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="fcp-current-input" autoFocus
              />
            </div>
            <div>
              <Label htmlFor="new" className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
              <Input
                id="new" type="password" value={next} maxLength={128}
                onChange={(e) => setNext(e.target.value)}
                data-testid="fcp-new-input"
              />
              <PasswordStrengthMeter
                password={next} userData={userData} role={user?.role}
              />
            </div>
            <div>
              <Label htmlFor="conf" className="text-xs uppercase tracking-wider">Repite la nueva contraseña</Label>
              <Input
                id="conf" type="password" value={confirm} maxLength={128}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="fcp-confirm-input"
              />
              {confirm && confirm !== next && (
                <p className="text-[11px] text-red-600 mt-1">No coinciden.</p>
              )}
            </div>

            <Button
              type="submit" disabled={!canSubmit}
              className="w-full bg-slate-900 hover:bg-black text-white uppercase tracking-wider text-xs h-10 rounded-sm"
              data-testid="fcp-submit-btn"
            >
              <KeyRound className="w-3.5 h-3.5 mr-2" />
              {loading ? "Guardando…" : "Guardar nueva contraseña"}
            </Button>

            <button
              type="button" onClick={() => { logout(); navigate("/login"); }}
              className="w-full text-[11px] text-slate-500 hover:text-slate-900 uppercase tracking-wider"
              data-testid="fcp-logout-btn"
            >
              Cerrar sesión
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
